import { EMAIL_TLDS } from "./email-tlds";

/**
 * Mail providers guests actually use, keyed by the part before the first dot,
 * with the endings each provider really hands out (the first is the one to
 * suggest when nothing else fits). Only these names are corrected: a typo in
 * a company domain cannot be told apart from a different company.
 */
const PROVIDERS = new Map<string, readonly string[]>(
  Object.entries({
    gmail: ["com"],
    googlemail: ["com"],
    hotmail: ["com", "nl", "be", "de", "fr", "it", "es", "co.uk"],
    outlook: ["com", "nl", "be", "de", "fr", "it", "es"],
    live: ["com", "nl", "be", "de", "fr", "it", "co.uk"],
    msn: ["com"],
    icloud: ["com"],
    me: ["com"],
    mac: ["com"],
    yahoo: ["com", "co.uk", "de", "fr", "es", "it"],
    ymail: ["com"],
    aol: ["com"],
    protonmail: ["com"],
    mozmail: ["com"],
    foxmail: ["com"],
    proton: ["me"],
    gmx: ["de", "net", "at", "ch", "com"],
    web: ["de"],
    "t-online": ["de"],
    online: ["de", "nl"],
    o2online: ["de"],
    freenet: ["de"],
    posteo: ["de"],
    mail: ["com", "de"],
    email: ["com"],
    ziggo: ["nl"],
    kpnmail: ["nl"],
    kpnplanet: ["nl"],
    planet: ["nl"],
    home: ["nl"],
    upcmail: ["nl"],
    chello: ["nl"],
    xs4all: ["nl"],
    hetnet: ["nl"],
    casema: ["nl"],
    quicknet: ["nl"],
    zeelandnet: ["nl"],
    telfort: ["nl"],
    telenet: ["be"],
    skynet: ["be"],
    proximus: ["be"],
    orange: ["fr"],
    wanadoo: ["fr"],
    free: ["fr"],
    sfr: ["fr"],
    laposte: ["net"],
    libero: ["it"],
    virgilio: ["it"],
    btinternet: ["com"],
    bluewin: ["ch"],
  }),
);

const PROVIDER_DOMAINS = [...PROVIDERS].flatMap(([name, endings]) =>
  endings.map((ending) => `${name}.${ending}`),
);

/**
 * Providers that only exist under their one domain, so any other ending is a
 * mistake (`gmail.nl`). Elsewhere a real ending is trusted: `orange.nl` and
 * `web.nl` are companies, not `orange.fr` and `web.de` mistyped.
 */
const SINGLE_DOMAIN_PROVIDERS = new Set(["gmail", "googlemail", "icloud"]);

/**
 * Endings offered when a company domain ends in something that is not a TLD,
 * most used first so a tie picks the likelier one (`.bl` → `.nl`, not `.be`).
 */
const COMMON_TLDS = [
  "com",
  "nl",
  "be",
  "de",
  "net",
  "org",
  "eu",
  "fr",
  "it",
  "es",
  "at",
  "ch",
  "info",
] as const;

/**
 * Real TLDs that are almost always `.com` with a key missed. They only count as
 * a typo on a provider name: `gmail.co` is a mistake, `acme.co` is a company.
 */
const COM_TYPO_TLDS = new Set(["co", "cm", "om"]);

/**
 * Optimal string alignment distance: Levenshtein plus swapping two adjacent
 * characters as a single edit, so `gamil` is one step from `gmail`.
 */
function editDistance(a: string, b: string): number {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[][] = Array.from({ length: rows }, (_, i) =>
    Array.from({ length: cols }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(
        d[i - 1]![j]! + 1,
        d[i]![j - 1]! + 1,
        d[i - 1]![j - 1]! + cost,
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, d[i - 2]![j - 2]! + 1);
      }
      d[i]![j] = best;
    }
  }

  return d[a.length]![b.length]!;
}

/**
 * The candidate closest to `value`, within `maxDistance`. A candidate that
 * `value` is the start of wins outright, so a half-typed `.c` becomes `.com`.
 */
function closest(
  value: string,
  candidates: readonly string[],
  maxDistance: number,
): string | null {
  const completion = candidates.find((candidate) =>
    candidate.startsWith(value),
  );
  if (value && completion) return completion;

  let best: string | null = null;
  let bestDistance = maxDistance + 1;
  for (const candidate of candidates) {
    const distance = editDistance(value, candidate);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }

  return best;
}

/**
 * How far a typed name may be from a provider name and still count as that
 * provider. Short names get no slack at all: `life.nl` and `wed.de` are real
 * domains, not `live.nl` and `web.de` misspelled.
 */
function providerNameSlack(name: string): number {
  if (name.length <= 4) return 0;
  if (name.length <= 6) return 1;
  return 2;
}

function splitEmail(email: string) {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0) return null;

  const domain = trimmed
    .slice(at + 1)
    .toLowerCase()
    .replace(/\.+$/, "");
  if (!domain) return null;

  return { local: trimmed.slice(0, at), domain };
}

function isKnownTld(tld: string): boolean {
  // Internationalised TLDs are not in the list; accept them by shape.
  if (tld.startsWith("xn--") || /[^\x00-\x7f]/.test(tld)) return true;
  return EMAIL_TLDS.has(tld);
}

function correctDomain(domain: string): string | null {
  if (PROVIDER_DOMAINS.includes(domain)) return null;

  // A dot left out altogether: `gmailcom`, `hotmailnl`.
  if (!domain.includes(".")) {
    return (
      PROVIDER_DOMAINS.find(
        (candidate) => candidate.replace(/\./g, "") === domain,
      ) ?? null
    );
  }

  const firstDot = domain.indexOf(".");
  const lastDot = domain.lastIndexOf(".");
  const name = domain.slice(0, firstDot);
  const ending = domain.slice(firstDot + 1);
  const tld = domain.slice(lastDot + 1);

  const provider = PROVIDERS.has(name)
    ? name
    : closest(
        name,
        [...PROVIDERS.keys()].filter(
          (candidate) =>
            editDistance(name, candidate) <= providerNameSlack(candidate),
        ),
        2,
      );

  if (provider) {
    const endings = PROVIDERS.get(provider)!;
    if (endings.includes(ending)) return `${provider}.${ending}`;

    const endingIsWrong =
      !isKnownTld(tld) ||
      COM_TYPO_TLDS.has(ending) ||
      (provider === name &&
        SINGLE_DOMAIN_PROVIDERS.has(provider) &&
        !ending.includes("."));
    if (!endingIsWrong) return null;

    return `${provider}.${closest(ending, endings, 2) ?? endings[0]}`;
  }

  if (isKnownTld(tld)) return null;

  const fixedTld = closest(tld, COMMON_TLDS, 1);
  if (!fixedTld) return null;
  return `${domain.slice(0, lastDot + 1)}${fixedTld}`;
}

/**
 * The address the guest most likely meant, or `null` when it looks right.
 * Only the domain is corrected, and only for misspelt mail providers
 * (`gamil.com`, `hotmail.con`) or an ending that does not exist
 * (`acme.vom` → `acme.com`). The result is a suggestion to offer, never
 * something to apply without asking: the guest may really mean it.
 */
export function suggestEmailCorrection(email: string): string | null {
  const parts = splitEmail(email);
  if (!parts) return null;

  const domain = correctDomain(parts.domain);
  if (!domain || domain === parts.domain) return null;

  return `${parts.local}@${domain}`;
}

/**
 * Whether the address ends in a top-level domain that exists. `gmail.con` and
 * `hotmail.vom` pass a format check but can never receive mail, so forms
 * should refuse them. Anything without a domain is left to the format check.
 */
export function hasKnownEmailTld(email: string): boolean {
  const parts = splitEmail(email);
  if (!parts) return true;

  const lastDot = parts.domain.lastIndexOf(".");
  if (lastDot === -1) return true;

  return isKnownTld(parts.domain.slice(lastDot + 1));
}
